/**
 * kanban-card-layout — wspólny renderer drzewa `cardLayout` (wygląd kafelka Kanban).
 *
 * `cardLayout` to `LayoutElement[]` w kształcie z `lib/layout-tree.ts` (inline
 * `children[]`) — ten sam model co kreator layoutu formularzy i ten sam, który
 * waliduje backend (`validateKanbanCardLayout`). Renderer chodzi rekurencyjnie po
 * drzewie i dla każdego węzła:
 *  - `type === 'field'` → deleguje do `renderField(fieldId)` (caller wie jak
 *    narysować wartość pola — w tablicy to komórka kolumny, w designerze placeholder),
 *  - inny typ → `LayoutElementRegistry.render({ element, children, mode })` z już
 *    zrenderowanymi dziećmi (kontenery: card/row/columns/grid/fieldset/panel; treść:
 *    heading/text/...; komunikaty: alert).
 *
 * Reużywany przez `KanbanBoard` (tryb `preview`/`form`) i `KanbanCardDesigner`
 * (podgląd `preview`).
 */

import { Fragment, type ReactNode } from 'react';

import {
  type LayoutElement as RegistryLayoutElement,
  LayoutElementRegistry,
  type LayoutRenderMode,
} from '@/components/fields/LayoutElementRegistry';
import type { LayoutElement } from '@/lib/layout-tree';

export function renderKanbanCardLayout(
  elements: LayoutElement[],
  renderField: (fieldId: string) => ReactNode,
  mode: LayoutRenderMode = 'preview',
): ReactNode[] {
  return elements.map((el) => {
    if (el.type === 'field') {
      return <Fragment key={el.id}>{el.fieldId ? renderField(el.fieldId) : null}</Fragment>;
    }
    const childNodes = el.children ? renderKanbanCardLayout(el.children, renderField, mode) : [];
    return (
      <Fragment key={el.id}>
        {LayoutElementRegistry.render({
          element: el as unknown as RegistryLayoutElement,
          children: childNodes,
          mode,
        })}
      </Fragment>
    );
  });
}

/** Czy `cardLayout` zawiera coś do narysowania (niepusta tablica). */
export function hasCardLayout(
  layout: LayoutElement[] | null | undefined,
): layout is LayoutElement[] {
  return Array.isArray(layout) && layout.length > 0;
}
